import React, { useEffect } from "react";
import { Sparkles } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useLocaleContext } from "../context/LocaleContext";
import { getTranslation } from "../utils/translations";
import {
  trackRybbitExitIntentTriggered,
  trackRybbitExitIntentAccepted,
  trackRybbitExitIntentDeclined
} from "../utils/rybbit";

interface ExitIntentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onLogout: () => void | Promise<void>;
}

const ExitIntentModal: React.FC<ExitIntentModalProps> = ({
  isOpen,
  onClose,
  onLogout,
}) => {
  const navigate = useNavigate();
  const { locale } = useLocaleContext();

  // Track when exit intent modal is shown
  useEffect(() => {
    if (isOpen) {
      if (typeof window !== 'undefined' && window.datafast) {
        window.datafast('goal', 'exit_intent_triggered', {});
      }
      trackRybbitExitIntentTriggered({
        user_tier: 'free',
        session_duration_minutes: 0,
        scans_in_session: 0,
        trigger_location: 'header_logout',
      });
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleViewSpecialDeal = () => {
    // Track that user accepted the exit intent offer
    if (typeof window !== 'undefined' && window.datafast) {
      window.datafast('goal', 'exit_intent_accepted', {});
    }
    trackRybbitExitIntentAccepted({
      user_tier: 'free',
    });
    onClose();
    navigate("/specialdeal");
  };

  const handleLogoutAnyway = async () => {
    // Track that user declined the exit intent offer
    if (typeof window !== 'undefined' && window.datafast) {
      window.datafast('goal', 'exit_intent_declined', {});
    }
    trackRybbitExitIntentDeclined();
    onClose();
    await onLogout();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-200">
      <div className="relative w-full max-w-lg bg-white dark:bg-gray-800 rounded-2xl shadow-2xl p-8 animate-in zoom-in-95 duration-300">
        {/* Icon */}
        <div className="flex justify-center mb-6">
          <div className="p-4 bg-gradient-to-br from-purple-500 to-pink-600 rounded-full">
            <Sparkles size={32} className="text-white" />
          </div>
        </div>

        {/* Heading */}
        <h2 className="text-3xl font-bold text-center mb-4 bg-gradient-to-r from-purple-600 to-pink-600 dark:from-purple-400 dark:to-pink-400 bg-clip-text text-transparent">
          {locale === "de" ? "Warten Sie!" : "Wait!"}
        </h2>
        <h3 className="text-2xl font-semibold text-center mb-6 text-gray-800 dark:text-gray-100">
          {locale === "de"
            ? "Wir haben ein besseres Angebot für Sie"
            : "We have a better deal for you"}
        </h3>

        {/* Body text */}
        <p className="text-center text-gray-600 dark:text-gray-300 mb-8 text-lg leading-relaxed">
          {locale === "de"
            ? "Wir möchten Sie nicht als Kunden verlieren. Lassen Sie uns Ihnen einen exklusiven Rabatt anbieten, der nur für Sie verfügbar ist."
            : "We don't want to lose you as a customer. Let us offer you an exclusive discount that's only available to you right now."}
        </p>

        {/* Special offer highlight */}
        <div className="bg-gradient-to-r from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 rounded-lg p-3 mb-8">
          <p className="text-center text-purple-800 dark:text-purple-200 text-base">
            {locale === "de"
              ? "🎁 Exklusiver Sonderpreis nur für Sie!"
              : "🎁 Exclusive special pricing just for you!"}
          </p>
        </div>

        {/* Buttons */}
        <div className="space-y-3">
          {/* Primary button - View Special Deal */}
          <button
            onClick={handleViewSpecialDeal}
            className="w-full px-6 py-4 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transform hover:scale-[1.02] transition-all duration-200 flex items-center justify-center gap-2"
          >
            <Sparkles size={20} />
            {locale === "de" ? "Sonderangebot ansehen" : "View Special Deal"}
          </button>

          {/* Secondary button - Logout anyway */}
          <button
            onClick={handleLogoutAnyway}
            className="w-full px-6 py-3 text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 font-medium transition-colors text-sm"
          >
            {locale === "de"
              ? "Abmelden und Sonderangebot verlieren"
              : "Log out and lose special deal"}
          </button>
        </div>

        {/* Fine print */}
        <p className="text-center text-xs text-gray-500 dark:text-gray-400 mt-6">
          {locale === "de"
            ? "Dieses Angebot ist nur für kurze Zeit verfügbar"
            : "This offer is available for a limited time only"}
        </p>
      </div>
    </div>
  );
};

export default ExitIntentModal;
